import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { useMappenlage } from "../daten/mappe";
import { nochEinmalVersuchen, useWarteschlange } from "../daten/warteschlange";
import type { Wartender } from "../daten/warteschlange";
import { fassungGewechselt } from "../pwa/anmelden";
import { useRuf } from "../schale/Ruf";
import { FASSUNG } from "../fassung";
import { hinweisWegklicken, installierenAnbieten, useInstallhinweis } from "../pwa/installieren";
import "../stile/offline.css";
import { uhrImBetrieb } from "../daten/betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Drei Bänder und ein Hinweis, die sagen, woran man ist.

   | `Netzband`           | „Ohne Netz: Du siehst deine Mappe …" |
   | `Warteschlangenband` | „3 Einträge warten auf Übertragung"  |
   | `Installhinweis`     | „Zum Startbildschirm hinzufügen"     |
   | `Fassungshinweis`    | „Neue Fassung geladen" — einmal, nach dem Wechsel |

   **Ehrlich statt leer.** 09-mobile.md nennt fünf Dinge am Telefon;
   ohne Netz gehen drei davon und zwei nicht. Verfügbarkeit, Preise und
   Angebote stehen in der Datenbank, und die ist nicht im Rucksack. Eine
   leere Liste behauptet „heute ist nichts" — und das ist etwas anderes
   als „ich weiß es gerade nicht".

   **Der Stand steht dabei.** Eine Mappe ohne Uhrzeit ist eine Liste, von
   der niemand weiß, ob sie von vorhin oder von gestern ist.
   ══════════════════════════════════════════════════════════════════ */

/** `07:12` in deutscher Zeit (N8-02). */
function uhr(iso: string): string {
  return uhrImBetrieb(iso);
}

/** Ob das Gerät gerade meint, es habe kein Netz. */
function useOhneNetz(): boolean {
  const [ohne, setzeOhne] = useState(() => navigator.onLine === false);
  useEffect(() => {
    const hin = () => setzeOhne(false);
    const weg = () => setzeOhne(true);
    window.addEventListener("online", hin);
    window.addEventListener("offline", weg);
    return () => {
      window.removeEventListener("online", hin);
      window.removeEventListener("offline", weg);
    };
  }, []);
  return ohne;
}

/**
 * Das ehrliche Band — in zwei Fassungen, je nachdem, was gerade wahr ist.
 *
 * `weg` ist derselbe Schlüssel wie in `daten/mappe.ts` (`handy/heute`,
 * `handy/einsatz`). Steht die Liste aus der Mappe da, sagt das Band es **mit
 * ihrem Stand** — eine Liste ohne Uhrzeit ist eine, von der niemand weiß, ob
 * sie von vorhin oder von gestern ist.
 *
 * Ohne `weg` — oder solange die Liste noch die frische von vorhin ist — bleibt
 * die andere Hälfte: **aufnehmen geht, nachschlagen nicht.** Das gilt auch
 * dort, wo es gar keine Liste gibt: im Protokoll und in der Vorbereitung.
 *
 * `navigator.onLine` allein wäre zu wenig: Ein Telefon im WLAN eines
 * Baucontainers ohne Gegenstelle ist „online" und bekommt trotzdem nichts.
 * Deshalb zählt die Mappe zuerst — sie weiß es aus einem echten Versuch.
 */
export function Netzband({ weg }: { weg?: string }) {
  const t = useT();
  const lage = useMappenlage(weg ?? "");
  const ohneNetz = useOhneNetz();

  if (lage.ausMappe) {
    return (
      <p className="netzband" role="status">
        <b>{t("offline.titel", { uhr: lage.stand ? uhr(lage.stand) : "" })}</b>
        <span>{t("offline.satz")}</span>
      </p>
    );
  }
  if (!ohneNetz) return null;
  return (
    <p className="netzband" role="status">
      <b>{t("offline.aufnehmenTitel")}</b>
      <span>{t("offline.aufnehmenSatz")}</span>
    </p>
  );
}

function Zeile({ w }: { w: Wartender }) {
  const t = useT();
  return (
    <li className={w.abgewiesen ? "wartet-zeile stockt" : "wartet-zeile"}>
      <span className="wartet-was">{t(`offline.art.${w.art}`)}</span>
      <span className="wartet-bezug">{w.betreff}</span>
      <span className="wartet-zeit">{uhr(w.erfasstAm)}</span>
      {w.grund ? <span className="wartet-grund">{t(w.grund, w.grundWerte)}</span> : null}
    </li>
  );
}

/**
 * „3 Einträge warten auf Übertragung" — mit Liste und einem Knopf.
 *
 * **Der Knopf ist nicht der Weg, sondern die Beruhigung.** Übertragen wird
 * von selbst, sobald Netz da ist; wer auf dem Hof steht und nichts passieren
 * sieht, soll trotzdem etwas drücken können.
 */
export function Warteschlangenband() {
  const t = useT();
  const wartende = useWarteschlange();
  const [offen, setzeOffen] = useState(false);
  const [laeuft, setzeLaeuft] = useState(false);
  if (!wartende.length) return null;

  const stockt = wartende.some((w) => w.abgewiesen);
  return (
    <section className={stockt ? "wartet stockt" : "wartet"} aria-label={t("offline.warteschlange")}>
      <button type="button" className="wartet-kopf" aria-expanded={offen} onClick={() => setzeOffen((x) => !x)}>
        <span className={`pkt ${stockt ? "stopp" : "acht"}`} aria-hidden="true" />
        <b>{t(wartende.length === 1 ? "offline.wartetEiner" : "offline.wartetMehrere", { zahl: wartende.length })}</b>
        <span className="leise">{t(offen ? "offline.zuklappen" : "offline.aufklappen")}</span>
      </button>
      {offen ? (
        <>
          <ul className="wartet-liste">{wartende.map((w) => <Zeile key={w.id} w={w} />)}</ul>
          {stockt ? <p className="wartet-hinweis">{t("offline.stocktSatz")}</p> : null}
        </>
      ) : null}
      <button
        type="button"
        className="handy-knopf leise"
        disabled={laeuft}
        onClick={() => {
          setzeLaeuft(true);
          void nochEinmalVersuchen().finally(() => setzeLaeuft(false));
        }}
      >
        {t(laeuft ? "offline.versuchtGerade" : "offline.jetztVersuchen")}
      </button>
    </section>
  );
}

/**
 * „Zum Startbildschirm hinzufügen" — einmal, und wegklickbar.
 *
 * Auf einem iPhone kann der Browser es nicht selbst anbieten; dort steht
 * der Weg als Satz da. Überall sonst fragt der Browser, und dieser Knopf
 * lässt ihn fragen.
 */
export function Installhinweis() {
  const t = useT();
  const lage = useInstallhinweis();
  if (!lage) return null;
  return (
    /* **Eine Zeile, keine Karte** (17.09.2026). Gemessen standen hier 210 px
       über der Tagesliste — zwei Knöpfe von 60 px für einen Hinweis, der
       einmal gelesen wird. Jetzt: der Satz links, die beiden Griffe rechts,
       48 px hoch. */
    <aside className="installhinweis">
      <div className="installhinweis-text">
        <b>{t("offline.installTitel")}</b>
        <span>{t(lage === "knopf" ? "offline.installSatzKurz" : "offline.installApfelKurz")}</span>
      </div>
      <div className="installhinweis-knoepfe">
        {lage === "knopf" ? (
          <button type="button" className="installhinweis-knopf ist-haupt" onClick={installierenAnbieten}>
            {t("offline.installJetzt")}
          </button>
        ) : null}
        <button type="button" className="installhinweis-knopf" onClick={hinweisWegklicken}>
          {t("offline.installSpaeter")}
        </button>
      </div>
    </aside>
  );
}

/**
 * „Neue Fassung geladen." — einmal, gleich nach dem Wechsel.
 *
 * **Bis zum 19.09.2026 stand hier ein Band mit „Laden" und „Später".** Luca:
 * *„Ich möchte, dass es automatisch aktualisiert wird."* Seitdem übernimmt die
 * Seite eine neue Fassung selbst, im sicheren Moment (`pwa/anmelden.ts`), und
 * der Knopf hat nichts mehr zu entscheiden. Er war ohnehin eine Frage, die auf
 * dem Hof niemand beantworten kann: Passt es gerade? Also wurde „Später"
 * getippt, und das Band kam wieder.
 *
 * **Übrig bleibt ein Satz, und zwar danach.** Das Neuladen ist kurz, aber
 * sichtbar — der Schirm blinkt einmal. Ohne ein Wort dazu sieht das aus wie ein
 * Fehler. Die Rückmeldung (`schale/Ruf.tsx`) steht drei Sekunden und
 * unterbricht nichts; sie kommt nur, wenn die Seite wirklich die neue Fassung
 * geworden ist.
 */
export function Fassungshinweis() {
  const ruf = useRuf();
  useEffect(() => {
    if (!fassungGewechselt()) return;
    if (FASSUNG.version) ruf("fassungGeladen", { version: FASSUNG.version });
    else ruf("fassungGeladenOhne");
  }, [ruf]);
  return null;
}
